import { FC, useState } from 'react';

import { useShopOrderCsMemoCache } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.hook';
import { ShopOrderCsMemo } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.interface';
import { CsMemoModalBase } from '@shared/components/csMemo';

import { shopOrderCsMemoAdapter } from './shopOrderCsMemoAdapter';

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}`;
};

/** 카드에 바로 펼쳐 보여줄 최근 메모 수 (나머지는 "+N" 표시 후 모달에서) */
const PREVIEW_COUNT = 2;

export interface ShopOrderCsMemoBlockProps {
  shopOrderId: string;
  /** 메모장 부제목 — 보내는 분/받는 분 등 */
  subtitle?: string;
  /**
   * 이미 아는 메모(컨테이너 배치 조회분). 안 주면 캐시를 구독하고,
   * 캐시도 없으면 개수 미상으로 두었다가 열 때 조회한다.
   */
  memos?: ShopOrderCsMemo[];
}

/**
 * 배송관리·과거배송검색 카드 안의 CS 응대 메모 블록.
 * 카드가 넓어서 **내용을 바로 보여주고**, 클릭하면 전체 타임라인 + 입력 모달을 연다.
 * 구매자·상주에게는 절대 노출되지 않는 스태프 전용 정보다.
 */
export const ShopOrderCsMemoBlock: FC<ShopOrderCsMemoBlockProps> = ({
  shopOrderId,
  subtitle,
  memos,
}) => {
  const [open, setOpen] = useState(false);
  const cached = useShopOrderCsMemoCache({
    shopOrder: memos === undefined ? shopOrderId : '',
  });

  const list = memos ?? cached ?? undefined;
  // 미배포(404)면 카드에 아무것도 붙이지 않는다
  if (cached === null) return null;

  const count = list?.length ?? 0;
  const recent = list ? list.slice(-PREVIEW_COUNT) : [];
  const hiddenCount = Math.max(count - recent.length, 0);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
        title="클릭해서 CS 응대 메모 보기·남기기"
        className={`w-full cursor-pointer rounded-lg border px-3 py-2 text-left transition-colors ${
          count > 0
            ? 'border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50'
            : 'border-dashed border-gray-300 bg-white hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-gray-700">
            CS 메모
            {count > 0 && (
              <span className="ml-1 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {count}
              </span>
            )}
          </p>
          <span className="text-[11px] font-medium text-cyan-700">
            {count > 0 ? '전체 보기' : '메모 남기기'}
          </span>
        </div>

        {count === 0 ? (
          <p className="mt-1 text-[11px] text-gray-400">
            응대 내용을 남기면 이 카드와 CS 응대 로그에서 함께 볼 수 있어요.
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {hiddenCount > 0 && (
              <li className="text-[11px] text-gray-400">이전 메모 {hiddenCount}건 더</li>
            )}
            {recent.map((memo) => (
              <li key={memo._id} className="text-xs leading-snug">
                <span className="mr-1 text-[11px] text-gray-400">
                  {fmtTime(memo.createdAt)}
                </span>
                <span className="mr-1 text-[11px] font-medium text-gray-600">
                  {memo.byName}
                </span>
                {memo.adminOnly && (
                  <span className="mr-1 rounded bg-amber-200 px-1 text-[10px] font-semibold text-amber-800">
                    관리자 전용
                  </span>
                )}
                {/* 긴 메모가 카드를 밀어내지 않게 2줄로 자른다 (전체는 클릭 시 모달).
                    line-clamp 유틸은 이 프로젝트 tailwind(3.2/플러그인 없음)에 없어 직접 지정 */}
                <span
                  className="whitespace-pre-wrap break-words text-gray-800"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {memo.body}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && (
        <CsMemoModalBase
          adapter={shopOrderCsMemoAdapter}
          title="주문 CS 응대 메모"
          subtitle={subtitle}
          target={{ shopOrder: shopOrderId }}
          memos={memos}
          onClose={() => setOpen(false)}
          zIndexClassName="z-[70]"
        />
      )}
    </>
  );
};
